import { WebViewerInstance, Core } from '@pdftron/webviewer';

import {
  ANNOTATION_BORDER_WIDTH,
  ANNOTATION_COLOR_CODES,
  ANNOTATION_OPACITY,
  SUGGESTION_SUBJECT,
} from './consts';
import type {
  SuggestionAnnotationRaw,
  SuggestionAnnotationState,
  SuggestionAnnotationDisplayType,
  SuggestionAnnotationRegionState,
} from './types';

/**
 * We cannot access/extend the CustomAnnotation class from the Core WebViewer object until it exists
 * at runtime. This is our second best option to get a strong typing for the SuggestionAnnotation class that we
 * define in buildSuggestionAnnotationClass.
 */
export interface ISuggestionAnnotation
  extends Core.Annotations.CustomAnnotation {
  show(): void;
  hide(): void;
  focus(): void;
  unfocus(): void;
  dim(): void;
  undim(): void;
  whiteout(): void;
  unwhiteout(): void;
  isDimmed(): boolean;
  isWhiteout(): boolean;
  isFocused(): boolean;
  getAppState(): SuggestionAnnotationState;
  setAppState(state: Partial<SuggestionAnnotationState>): void;
  rawAnnotation: SuggestionAnnotationRaw;
  partFileUuid: string;
}

export type SuggestionAnnotationClassType = new (
  rawAnnotation: SuggestionAnnotationRaw,
) => ISuggestionAnnotation;

// ============================================================================
// CORE DRAWING FUNCTIONS
// ============================================================================
// Low-level, reusable drawing primitives that handle basic canvas operations

/**
 * Converts a hex color to hex with alpha value
 * @param hexColor - Hex color string (e.g., "#8C41ED")
 * @param alpha - Alpha value between 0 and 1
 * @returns Hex color with alpha (e.g., "#8C41ED55")
 */
function hexWithAlpha(hexColor: string, alpha: number): string {
  // Convert alpha (0-1) to hex (00-FF)
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hexColor.slice(0, 7)}${alphaHex}`;
}

/**
 * Draws a fill with the specified color and opacity
 */
function drawFill(
  ctx: CanvasRenderingContext2D,
  color: string,
  opacity: number,
) {
  const fillColor = hexWithAlpha(color, opacity);
  ctx.fillStyle = fillColor;
  ctx.fill();
}

/**
 * Draws an outline with the specified color and opacity
 * Scales line width with zoom to maintain consistent appearance
 */
function drawOutline(
  ctx: CanvasRenderingContext2D,
  color: string,
  opacity: number,
  zoom: number,
  lineWidth = 1,
) {
  const strokeColor = hexWithAlpha(color, opacity);
  ctx.strokeStyle = strokeColor;
  // Scale the line width based on zoom level for consistent visual appearance
  ctx.lineWidth = lineWidth / zoom;
  ctx.stroke();
}

/**
 * Draws realistic blood dripping effect with more organic, viscous appearance
 */
function drawRealisticBloodDrips(
  ctx: CanvasRenderingContext2D,
  bounds: AnnotationBounds,
) {
  const bloodOrange = ANNOTATION_COLOR_CODES.BLEEDING_ORANGE; // Deep orange color
  const bloodOrangeLight = ANNOTATION_COLOR_CODES.BLEEDING_ORANGE_LIGHT; // Lighter orange for highlights
  const { x, y, width, height } = bounds;
  const bottomY = y + height;
  const borderRadius = 5;

  // Calculate valid range accounting for border radius
  const minX = x + borderRadius;
  const maxX = x + width - borderRadius;
  const validWidth = maxX - minX;

  // Create fewer but more prominent drips
  const dripCount = Math.max(2, Math.floor(width / 30));
  const dripData: Array<{
    dripX: number;
    dripLength: number;
    baseWidth: number;
  }> = [];

  for (let i = 0; i < dripCount; i++) {
    const seed = (x + i * 137) % 100;
    const dripX = minX + (seed / 100) * validWidth;

    // Vary drip characteristics more dramatically
    const lengthSeed = (x + i * 523) % 100;
    const dripLength = 12 + (lengthSeed / 100) * 20;

    const widthSeed = (x + i * 847) % 100;
    const baseWidth = 3 + (widthSeed / 100) * 5;

    // Store drip data for later droplet positioning
    dripData.push({ dripX, dripLength, baseWidth });

    // Draw main drip body with irregular edges
    ctx.beginPath();
    ctx.moveTo(dripX - baseWidth / 2, bottomY);

    // Create organic curves with multiple control points for irregular edges
    const segments = 5;
    let currentX = dripX - baseWidth / 2;
    let currentY = bottomY;

    for (let seg = 0; seg <= segments; seg++) {
      const t = seg / segments;
      const segmentY = bottomY + dripLength * t;

      // Taper width as we go down, with some irregularity
      const irregularitySeed = (x + i * 100 + seg * 50) % 100;
      const irregularity = 0.8 + (irregularitySeed / 100) * 0.4;
      const segmentWidth = baseWidth * (1 - t * 0.7) * irregularity;

      // Add slight wobble to left edge
      const wobbleSeed = (x + i * 200 + seg * 30) % 100;
      const wobble = (wobbleSeed / 100 - 0.5) * baseWidth * 0.3;

      if (seg === 0) {
        currentX = dripX - segmentWidth / 2 + wobble;
        currentY = segmentY;
      } else if (seg === segments) {
        // End in a point
        ctx.quadraticCurveTo(currentX, currentY, dripX, segmentY);
      } else {
        const nextX = dripX - segmentWidth / 2 + wobble;
        ctx.quadraticCurveTo(currentX, currentY, nextX, segmentY);
        currentX = nextX;
        currentY = segmentY;
      }
    }

    // Draw right side back up
    currentX = dripX;
    currentY = bottomY + dripLength;

    for (let seg = segments; seg >= 0; seg--) {
      const t = seg / segments;
      const segmentY = bottomY + dripLength * t;

      const irregularitySeed = (x + i * 100 + seg * 50) % 100;
      const irregularity = 0.8 + (irregularitySeed / 100) * 0.4;
      const segmentWidth = baseWidth * (1 - t * 0.7) * irregularity;

      const wobbleSeed = (x + i * 200 + seg * 30 + 1000) % 100;
      const wobble = (wobbleSeed / 100 - 0.5) * baseWidth * 0.3;

      if (seg === 0) {
        ctx.quadraticCurveTo(
          currentX,
          currentY,
          dripX + baseWidth / 2,
          bottomY,
        );
      } else {
        const nextX = dripX + segmentWidth / 2 + wobble;
        ctx.quadraticCurveTo(currentX, currentY, nextX, segmentY);
        currentX = nextX;
        currentY = segmentY;
      }
    }

    ctx.closePath();

    // Fill with gradient effect using multiple layers
    drawFill(ctx, bloodOrange, 0.85);

    // Add highlights/texture with lighter color in center
    ctx.beginPath();
    ctx.moveTo(dripX - baseWidth / 4, bottomY);
    ctx.lineTo(dripX - baseWidth / 8, bottomY + dripLength * 0.7);
    ctx.lineTo(dripX, bottomY + dripLength);
    ctx.lineTo(dripX + baseWidth / 8, bottomY + dripLength * 0.7);
    ctx.lineTo(dripX + baseWidth / 4, bottomY);
    ctx.closePath();
    drawFill(ctx, bloodOrangeLight, 0.4);
  }

  // Add continuous thin film of blood along the bottom edge
  // This mimics the accumulation that forms before drips fall
  ctx.beginPath();
  ctx.moveTo(minX, bottomY);

  // Use smooth sine wave with subtle fluctuations
  const filmSegments = Math.max(20, Math.floor(validWidth / 4));
  const maxThickness = 3.5;

  for (let i = 0; i <= filmSegments; i++) {
    const t = i / filmSegments;
    const segmentX = minX + t * validWidth;

    // Main sine wave envelope - starts at 0, peaks in middle, returns to 0
    const sineEnvelope = Math.sin(t * Math.PI);
    const baseThickness = sineEnvelope * maxThickness;

    // Add subtle high-frequency fluctuations (like a ripple filter)
    const fluctuationFrequency = 8;
    const fluctuationSeed = (x + i * 127) % 100;
    const fluctuationPhase = (fluctuationSeed / 100) * Math.PI * 0.5;
    const fluctuation =
      Math.sin(t * Math.PI * fluctuationFrequency + fluctuationPhase) * 0.8;

    // Combine envelope with fluctuations, ensuring smooth taper at edges
    const edgeY = bottomY + baseThickness + fluctuation * sineEnvelope;

    if (i === 0) {
      ctx.lineTo(segmentX, edgeY);
    } else {
      ctx.lineTo(segmentX, edgeY);
    }
  }

  // Close the path back along the bottom edge
  ctx.lineTo(maxX, bottomY);
  ctx.lineTo(minX, bottomY);
  ctx.closePath();

  // Draw with high opacity
  drawFill(ctx, bloodOrange, 0.88);

  // Add a second layer with lighter color for depth/shininess
  ctx.beginPath();
  ctx.moveTo(minX, bottomY);

  for (let i = 0; i <= filmSegments; i++) {
    const t = i / filmSegments;
    const segmentX = minX + t * validWidth;

    const sineEnvelope = Math.sin(t * Math.PI);
    const baseThickness = sineEnvelope * maxThickness;

    const fluctuationFrequency = 8;
    const fluctuationSeed = (x + i * 127) % 100;
    const fluctuationPhase = (fluctuationSeed / 100) * Math.PI * 0.5;
    const fluctuation =
      Math.sin(t * Math.PI * fluctuationFrequency + fluctuationPhase) * 0.8;

    // Draw highlight at 50% of thickness
    const highlightY =
      bottomY + (baseThickness + fluctuation * sineEnvelope) * 0.5;

    if (i === 0) {
      ctx.lineTo(segmentX, highlightY);
    } else {
      ctx.lineTo(segmentX, highlightY);
    }
  }

  ctx.lineTo(maxX, bottomY);
  ctx.lineTo(minX, bottomY);
  ctx.closePath();
  drawFill(ctx, bloodOrangeLight, 0.35);

  // Add realistic teardrop droplets at the end of each drip
  for (let i = 0; i < dripData.length; i++) {
    const { dripX, dripLength, baseWidth } = dripData[i];
    const seed = (x + i * 419) % 100;

    // Position droplet at the end of the drip with slight offset
    const dropletX = dripX + (seed / 100 - 0.5) * baseWidth * 0.3;
    const dropletY = bottomY + dripLength + 2 + (seed / 100) * 3;
    const dropletRadius = 1.5 + (seed / 100) * 2.5;

    // Draw teardrop shape with organic curves
    ctx.beginPath();
    // Start at the point (top of teardrop)
    const pointY = dropletY - dropletRadius * 1.4;
    ctx.moveTo(dropletX, pointY);

    // Right side curve with slight irregularity
    const irregularitySeed = (seed + i * 50) % 100;
    const rightBulge = 0.65 + (irregularitySeed / 100) * 0.15;
    ctx.bezierCurveTo(
      dropletX + dropletRadius * 0.6,
      dropletY - dropletRadius * 0.9,
      dropletX + dropletRadius * rightBulge,
      dropletY - dropletRadius * 0.3,
      dropletX + dropletRadius * rightBulge,
      dropletY,
    );

    // Bottom arc (right to left)
    ctx.arc(dropletX, dropletY, dropletRadius * rightBulge, 0, Math.PI, false);

    // Left side curve
    const leftBulge = 0.65 + (((irregularitySeed + 50) % 100) / 100) * 0.15;
    ctx.bezierCurveTo(
      dropletX - dropletRadius * leftBulge,
      dropletY - dropletRadius * 0.3,
      dropletX - dropletRadius * 0.6,
      dropletY - dropletRadius * 0.9,
      dropletX,
      pointY,
    );

    ctx.closePath();
    drawFill(ctx, bloodOrange, 0.8);

    // Add subtle highlight on larger drops
    if (dropletRadius > 2.5) {
      ctx.beginPath();
      ctx.ellipse(
        dropletX + dropletRadius * 0.15,
        dropletY - dropletRadius * 0.2,
        dropletRadius * 0.2,
        dropletRadius * 0.3,
        0,
        0,
        Math.PI * 2,
      );
      ctx.closePath();
      drawFill(ctx, bloodOrangeLight, 0.3);
    }
  }
}

// ============================================================================
// REGION ANNOTATION DRAWING UTILITIES
// ============================================================================

/**
 * Creates a diagonal stripe pattern for region annotations
 * @param ctx - Canvas rendering context
 * @param color - Base color for the pattern
 * @param opacity - Opacity of the pattern
 * @param stripeWidth - Desired visual width of each stripe on screen (in pixels)
 * @param stripeSpacing - Desired visual spacing between stripes on screen (in pixels)
 * @param zoom - Current zoom level
 * @returns CanvasPattern object
 */
function createDiagonalStripePattern(
  ctx: CanvasRenderingContext2D,
  color: string,
  opacity: number,
  stripeWidth = 1.5,
  stripeSpacing = 8,
  zoom = 1,
): CanvasPattern | null {
  const patternCanvas = document.createElement('canvas');
  const patternCtx = patternCanvas.getContext('2d');
  if (!patternCtx) return null;

  const d = Math.max(1, Math.floor(stripeSpacing / zoom));
  const w = stripeWidth / zoom;

  patternCanvas.width = d;
  patternCanvas.height = d;

  const patternColor = hexWithAlpha(color, opacity);
  patternCtx.strokeStyle = patternColor;
  patternCtx.lineWidth = w;

  // 'butt' prevents rounded ends from blurring the pattern on a small canvas
  patternCtx.lineCap = 'butt';

  // Create diagonal line from bottom-left to top-right
  patternCtx.beginPath();
  patternCtx.moveTo(0, d);
  patternCtx.lineTo(d, 0);
  patternCtx.stroke();

  const pattern = ctx.createPattern(patternCanvas, 'repeat');

  return pattern;
}

/**
 * Creates a feathered mask for region annotations that creates a frame-like effect
 * @param bounds - Bounding box for the annotation
 * @param featherSize - Size of the feathered edge in pixels
 * @returns Canvas element with the mask
 */
function createFeatheredMask(
  bounds: AnnotationBounds,
  featherSize: number,
): HTMLCanvasElement | null {
  const { width, height } = bounds;

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = width;
  maskCanvas.height = height;
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) return null;

  // Create vertical gradient (opaque top/bottom, transparent middle)
  const gradY = maskCtx.createLinearGradient(0, 0, 0, height);
  const startRatioY = featherSize / height;
  const endRatioY = 1 - startRatioY;
  gradY.addColorStop(0, 'white');
  gradY.addColorStop(startRatioY, 'transparent');
  gradY.addColorStop(endRatioY, 'transparent');
  gradY.addColorStop(1, 'white');

  maskCtx.fillStyle = gradY;
  maskCtx.fillRect(0, 0, width, height);

  // Create horizontal gradient (opaque left/right, transparent middle)
  const gradX = maskCtx.createLinearGradient(0, 0, width, 0);
  const startRatioX = featherSize / width;
  const endRatioX = 1 - startRatioX;
  gradX.addColorStop(0, 'white');
  gradX.addColorStop(startRatioX, 'transparent');
  gradX.addColorStop(endRatioX, 'transparent');
  gradX.addColorStop(1, 'white');

  // Use 'lighter' to combine the gradients, creating a frame-like mask
  maskCtx.globalCompositeOperation = 'lighter';
  maskCtx.fillStyle = gradX;
  maskCtx.fillRect(0, 0, width, height);

  return maskCanvas;
}

/**
 * Draws the diagonal stripe pattern with feathered masking for region annotations
 * @param ctx - Canvas rendering context
 * @param bounds - Bounding box for the annotation
 * @param color - Color for the stripes
 * @param patternOpacity - Opacity of the stripe pattern
 * @param zoom - Current zoom level
 */
function drawRegionPattern(
  ctx: CanvasRenderingContext2D,
  bounds: AnnotationBounds,
  color: string,
  patternOpacity: number,
  zoom: number,
) {
  const stripeWidth = 1.5;
  const stripeSpacing = 8;
  const { x, y, width, height } = bounds;
  const featherSize = Math.min(30 / zoom, width / 4, height / 4);

  const pattern = createDiagonalStripePattern(
    ctx,
    color,
    patternOpacity,
    stripeWidth,
    stripeSpacing,
    zoom,
  );

  if (pattern) {
    // Create temporary canvas for masking
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');

    if (tempCtx) {
      // Draw pattern on temp canvas
      tempCtx.fillStyle = pattern;
      tempCtx.fillRect(0, 0, width, height);

      // Apply feathered mask
      const mask = createFeatheredMask(bounds, featherSize);
      if (mask) {
        tempCtx.globalCompositeOperation = 'destination-in';
        tempCtx.drawImage(mask, 0, 0);
      }

      // Draw the masked result to main canvas
      ctx.drawImage(tempCanvas, x, y);
    }
  }
}

/**
 * Draws a label for region annotations at the top-left corner with a white background
 * @param ctx - Canvas rendering context
 * @param bounds - Bounding box for the annotation
 * @param label - Label text
 * @param color - Label color
 * @param fontSize - Font size in pixels
 * @param zoom - Current zoom level
 */
function drawRegionLabel(
  ctx: CanvasRenderingContext2D,
  bounds: AnnotationBounds,
  label: string,
  color: string,
  fontSize = 12,
  zoom = 1,
): void {
  const { x, y } = bounds;

  ctx.save();

  // Reset composite operation to allow white background to render
  // (parent context uses 'darken' which prevents white from showing)
  ctx.globalCompositeOperation = 'source-over';

  // Scale font size inversely with zoom to maintain consistent visual size
  const scaledFontSize = fontSize / zoom;
  ctx.font = `bold ${scaledFontSize}px Libre Franklin`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // Calculate label position (always top-left) - scale padding with zoom
  const paddingX = 8 / zoom;
  const paddingY = -2 / zoom;
  const labelX = x + paddingX;
  const labelY = y + paddingY + scaledFontSize - 2 / zoom;

  // Estimate text width for background (avoids measureText on every draw call)
  // For bold sans-serif fonts, ~0.6x font size per character is a good approximation
  const avgCharWidth = scaledFontSize * 0.6;
  const textWidth = label.length * avgCharWidth;
  const textHeight = scaledFontSize;

  // Background padding
  const bgPaddingX = 4 / zoom;
  const bgPaddingY = 2 / zoom;

  // Draw white background with rounded corners
  const bgX = labelX - bgPaddingX;
  const bgY = labelY - bgPaddingY;
  const bgWidth = textWidth + bgPaddingX * 2;
  const bgHeight = textHeight + bgPaddingY * 2;
  const bgRadius = 3 / zoom;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.beginPath();
  ctx.moveTo(bgX + bgRadius, bgY);
  ctx.lineTo(bgX + bgWidth - bgRadius, bgY);
  ctx.quadraticCurveTo(bgX + bgWidth, bgY, bgX + bgWidth, bgY + bgRadius);
  ctx.lineTo(bgX + bgWidth, bgY + bgHeight - bgRadius);
  ctx.quadraticCurveTo(
    bgX + bgWidth,
    bgY + bgHeight,
    bgX + bgWidth - bgRadius,
    bgY + bgHeight,
  );
  ctx.lineTo(bgX + bgRadius, bgY + bgHeight);
  ctx.quadraticCurveTo(bgX, bgY + bgHeight, bgX, bgY + bgHeight - bgRadius);
  ctx.lineTo(bgX, bgY + bgRadius);
  ctx.quadraticCurveTo(bgX, bgY, bgX + bgRadius, bgY);
  ctx.closePath();
  ctx.fill();

  // Draw label text
  ctx.fillStyle = color;
  ctx.fillText(label, labelX, labelY);

  ctx.restore();
}

// ============================================================================
// UNIFIED ANNOTATION DRAWING FUNCTION
// ============================================================================

type AnnotationState =
  | 'whiteout'
  | 'whiteout-hover'
  | 'dimmed'
  | 'default'
  | 'hover'
  | 'focus';

interface AnnotationBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

type DrawAnnotationParams = {
  ctx: CanvasRenderingContext2D;
  bounds: AnnotationBounds;
  mainColor: string;
  mainOpacity: number;
  mainBorderWidth: number;
  state: AnnotationState;
  zoom: number;
  bleedOrange: boolean;
  displayType: SuggestionAnnotationDisplayType;
  regionState?: SuggestionAnnotationRegionState;
};

/**
 * Annotation drawing function that handles all states and display types
 */
function drawAnnotation({
  ctx,
  bounds,
  mainColor,
  mainOpacity,
  mainBorderWidth,
  state,
  zoom,
  bleedOrange,
  displayType,
  regionState,
}: DrawAnnotationParams) {
  ctx.save();

  // Set blend mode based on state
  if (state === 'whiteout' || state === 'whiteout-hover') {
    ctx.globalCompositeOperation = 'source-over';
  } else {
    ctx.globalCompositeOperation = 'darken';
  }

  const color = bleedOrange
    ? ANNOTATION_COLOR_CODES.BLEEDING_ORANGE
    : mainColor;

  // Create rounded rectangle path
  const { x, y, width, height } = bounds;
  const isRegion = displayType === 'region';
  const radius = isRegion ? 8 : 3; // Larger radius for regions
  const scaledRadius = radius / zoom; // Scale radius with zoom level
  const adjustedRadius = Math.min(scaledRadius, width / 2, height / 2);

  ctx.beginPath();
  ctx.moveTo(x + adjustedRadius, y);
  ctx.lineTo(x + width - adjustedRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + adjustedRadius);
  ctx.lineTo(x + width, y + height - adjustedRadius);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - adjustedRadius,
    y + height,
  );
  ctx.lineTo(x + adjustedRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - adjustedRadius);
  ctx.lineTo(x, y + adjustedRadius);
  ctx.quadraticCurveTo(x, y, x + adjustedRadius, y);
  ctx.closePath();

  // Apply state-specific drawing logic based on display type
  if (isRegion) {
    // Region-specific drawing with diagonal pattern and masking
    switch (state) {
      case 'whiteout': {
        // Draw whiteout for regions
        const whiteoutColor = '#FFFFFF';
        drawFill(ctx, whiteoutColor, ANNOTATION_OPACITY.REGION_WHITEOUT);
        break;
      }

      case 'whiteout-hover': {
        // Draw base outline
        drawOutline(
          ctx,
          color,
          ANNOTATION_OPACITY.DEFAULT_OUTLINE,
          zoom,
          ANNOTATION_BORDER_WIDTH.REGION_OUTLINE_FOCUS,
        );
        // Draw pattern with masking
        drawRegionPattern(
          ctx,
          bounds,
          color,
          ANNOTATION_OPACITY.REGION_PATTERN_FOCUS,
          zoom,
        );
        // Draw outer stroke
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5 / zoom;
        ctx.stroke();
        break;
      }

      case 'dimmed': {
        // Draw subtle pattern with masking only - no outline or label in dimmed mode
        drawRegionPattern(ctx, bounds, color, mainOpacity, zoom);
        break;
      }

      case 'default': {
        // Draw base outline
        if (regionState && regionState.outlineOpacity > 0) {
          drawOutline(
            ctx,
            color,
            regionState.outlineOpacity,
            zoom,
            regionState.outlineWidth,
          );
        }
        // Draw pattern with masking
        drawRegionPattern(
          ctx,
          bounds,
          color,
          regionState?.patternOpacity ??
            ANNOTATION_OPACITY.REGION_PATTERN_DEFAULT,
          zoom,
        );
        // Draw outer stroke
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5 / zoom;
        ctx.stroke();
        break;
      }

      case 'hover': {
        // Draw base outline
        drawOutline(
          ctx,
          color,
          ANNOTATION_OPACITY.DEFAULT_OUTLINE,
          zoom,
          ANNOTATION_BORDER_WIDTH.REGION_OUTLINE_FOCUS,
        );
        // Draw enhanced pattern with masking
        drawRegionPattern(
          ctx,
          bounds,
          color,
          ANNOTATION_OPACITY.REGION_PATTERN_FOCUS,
          zoom,
        );
        // Draw outer stroke
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5 / zoom;
        ctx.stroke();
        break;
      }

      case 'focus': {
        // Draw base outline
        drawOutline(
          ctx,
          color,
          ANNOTATION_OPACITY.DEFAULT_OUTLINE,
          zoom,
          ANNOTATION_BORDER_WIDTH.REGION_OUTLINE_FOCUS,
        );
        // Draw strong pattern with masking
        drawRegionPattern(
          ctx,
          bounds,
          color,
          ANNOTATION_OPACITY.REGION_PATTERN_FOCUS,
          zoom,
        );
        // Draw outer stroke
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5 / zoom;
        ctx.stroke();
        break;
      }
    }

    // Draw label if provided for region display type
    // (skip in dimmed mode or if 0 outline opacity)
    if (
      regionState &&
      regionState.label &&
      state !== 'dimmed' &&
      regionState.outlineOpacity > 0
    ) {
      drawRegionLabel(ctx, bounds, regionState.label, color, 12, zoom);
    }
  } else {
    // Content-specific drawing (original logic)
    switch (state) {
      case 'whiteout': {
        // Draw 95% fill to cover the content
        const whiteoutColor = '#FFFFFF';
        drawFill(ctx, whiteoutColor, ANNOTATION_OPACITY.WHITEOUT);
        break;
      }

      case 'whiteout-hover': {
        // Then draw 15% fill so people can still know the category
        drawFill(ctx, color, mainOpacity);
        // Add hover outline
        drawOutline(
          ctx,
          color,
          ANNOTATION_OPACITY.DEFAULT_OUTLINE,
          zoom,
          ANNOTATION_BORDER_WIDTH.HOVER,
        );
        break;
      }

      case 'dimmed':
        // Draw fill with dimmed opacity, no outline
        drawFill(ctx, color, mainOpacity);
        break;

      case 'default':
        // Draw fill with default opacity and 1px outline
        drawFill(ctx, color, mainOpacity);
        if (mainBorderWidth > 0) {
          drawOutline(
            ctx,
            color,
            ANNOTATION_OPACITY.DEFAULT_OUTLINE,
            zoom,
            bleedOrange
              ? ANNOTATION_BORDER_WIDTH.BLEEDING_ORANGE
              : mainBorderWidth,
          );
        }
        break;

      case 'hover':
        // Draw fill with hover opacity (15%) and 3px outline
        drawFill(ctx, color, mainOpacity);
        drawOutline(
          ctx,
          color,
          ANNOTATION_OPACITY.DEFAULT_OUTLINE,
          zoom,
          ANNOTATION_BORDER_WIDTH.HOVER,
        );
        break;

      case 'focus':
        // Draw fill with focused opacity (30%) and 3px outline
        drawFill(ctx, color, ANNOTATION_OPACITY.FOCUS);
        drawOutline(
          ctx,
          color,
          ANNOTATION_OPACITY.DEFAULT_OUTLINE,
          zoom,
          ANNOTATION_BORDER_WIDTH.FOCUS,
        );
        break;
    }

    // Apply blood drip effect if specified (only for content type)
    if (bleedOrange) {
      drawRealisticBloodDrips(ctx, bounds);
    }
  }

  ctx.restore();
}

export const buildSuggestionAnnotationClass = ({
  webViewerInstance,
  annotationManager,
}: {
  webViewerInstance: WebViewerInstance;
  annotationManager: Core.AnnotationManager;
}): SuggestionAnnotationClassType => {
  const { Annotations } = webViewerInstance.Core;

  class CustomSelectionModel extends Annotations.SelectionModel {
    constructor(
      annotation: Core.Annotations.Annotation,
      canModify: boolean,
      isSelected: boolean,
      docViewer: Core.DocumentViewer,
    ) {
      super(annotation, canModify, isSelected, docViewer);
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */
    drawSelectionOutline(
      ctx: CanvasRenderingContext2D,
      annotation: Core.Annotations.Annotation,
      zoom?: number,
    ) {
      // we intentionally do not want to draw anything here
    }
  }

  class SuggestionAnnotation
    extends Annotations.CustomAnnotation
    implements ISuggestionAnnotation
  {
    documentViewer: Core.DocumentViewer;
    annotationManager: Core.AnnotationManager;
    rawAnnotation: SuggestionAnnotationRaw;
    partFileUuid: string;
    colorCode: string;
    opacity: number;
    borderWidth: number;
    displayType: SuggestionAnnotationDisplayType;
    regionState?: SuggestionAnnotationRegionState;
    private _isBleedingOrange: boolean;
    private _isFocused: boolean;
    private _isWhiteout: boolean;
    private _isDimmed: boolean;

    constructor(rawAnnotation: SuggestionAnnotationRaw) {
      super('suggestion'); // provide the custom XFDF element name
      this.Subject = SUGGESTION_SUBJECT;
      // NB: No matter what, TypeScript does not play nice with CustomSelectionModel and the typedef in Core.Annotations.SelectionModel
      // FIXME: Check on this after the Apryse/PDFTron v11 update and see if they're more in sync.
      this.selectionModel =
        CustomSelectionModel as unknown as Core.Annotations.SelectionModel;
      this.documentViewer = webViewerInstance.Core.documentViewer;
      this.annotationManager = this.documentViewer.getAnnotationManager();
      this.rawAnnotation = rawAnnotation;
      this.colorCode = rawAnnotation.colorCode;
      this.opacity = rawAnnotation.opacity;
      this.borderWidth = rawAnnotation.borderWidth;
      this.displayType = rawAnnotation.displayType ?? 'content';
      this.regionState = rawAnnotation.defaultRegionState;
      this.partFileUuid = rawAnnotation.partFileId;
      // Limit interaction with the annotations
      this.NoMove = true;
      this.NoResize = true;
      this.NoRotate = true;
      this.NoDelete = true;
      this.Printable = false; // Exclude from prints
      this.BlendMode = 'darken'; // to avoid hiding content or cursing the color
      this.IsHoverable = true; // Enable hover detection
      this._isFocused = false;
      this._isWhiteout = false;
      this._isDimmed = false;
      this._isBleedingOrange = false;
    }

    show() {
      this.Hidden = false;
    }

    hide() {
      this.Hidden = true;
    }

    focus() {
      this._isFocused = true;
    }

    unfocus() {
      this._isFocused = false;
    }

    whiteout() {
      this._isWhiteout = true;
    }

    unwhiteout() {
      this._isWhiteout = false;
    }

    dim() {
      this._isDimmed = true;
    }

    undim() {
      this._isDimmed = false;
    }

    isDimmed() {
      return this._isDimmed;
    }

    isWhiteout() {
      return this._isWhiteout;
    }

    isFocused() {
      return this._isFocused;
    }

    getAppState() {
      return {
        isHidden: this.Hidden,
        isFocused: this._isFocused,
        isWhiteout: this._isWhiteout,
        isDimmed: this._isDimmed,
        colorCode: this.colorCode,
        opacity: this.opacity,
        borderWidth: this.borderWidth,
        isBleedingOrange: this._isBleedingOrange,
        displayType: this.displayType,
        regionState: this.regionState,
      };
    }

    setAppState(state: Partial<SuggestionAnnotationState>) {
      if (state.isHidden !== undefined) {
        this.Hidden = state.isHidden;
      }
      if (state.isFocused !== undefined) {
        this._isFocused = state.isFocused;
      }
      if (state.isWhiteout !== undefined) {
        this._isWhiteout = state.isWhiteout;
      }
      if (state.isDimmed !== undefined) {
        this._isDimmed = state.isDimmed;
      }
      if (state.colorCode !== undefined) {
        this.colorCode = state.colorCode;
      }
      if (state.opacity !== undefined) {
        this.opacity = state.opacity;
      }
      if (state.borderWidth !== undefined) {
        this.borderWidth = state.borderWidth;
      }
      if (state.isBleedingOrange !== undefined) {
        this._isBleedingOrange = state.isBleedingOrange;
      }
      if (state.displayType !== undefined) {
        this.displayType = state.displayType;
      }
      if (state.regionState !== undefined) {
        if (this.regionState === undefined) {
          this.regionState = state.regionState;
        } else {
          this.regionState = {
            ...this.regionState,
            ...state.regionState,
          };
        }
      }
    }

    draw(ctx: CanvasRenderingContext2D, pageMatrix: number[]) {
      const zoom = this.documentViewer.getZoomLevel() || 1;

      ctx.save();
      // Apply the pageMatrix transformation
      ctx.setTransform(
        pageMatrix[0],
        pageMatrix[1],
        pageMatrix[2],
        pageMatrix[3],
        pageMatrix[4],
        pageMatrix[5],
      );

      // Content annotations should be brought to front when focused
      // Region annotations should never be brought to front so content annotations within them remain clickable
      if (this._isFocused && this.displayType !== 'region') {
        this.annotationManager.bringToFront(this);
      }
      // if whiteout make unlistable using built in PDFTron property
      if (this._isWhiteout) {
        this.Listable = false;
      } else {
        this.Listable = true;
      }

      // Check if annotation is being hovered using PDFTron's IsHovering property
      const isHovered = this.IsHovering;

      // Determine annotation state and use unified drawing function
      let annotationState: AnnotationState;
      if (this._isFocused && isHovered) {
        annotationState = 'focus';
      } else if (this._isFocused) {
        annotationState = 'focus';
      } else if (this._isWhiteout && isHovered) {
        annotationState = 'whiteout-hover';
      } else if (this._isWhiteout) {
        annotationState = 'whiteout';
      } else if (isHovered) {
        annotationState = 'hover';
      } else if (this._isDimmed) {
        annotationState = 'dimmed';
      } else {
        annotationState = 'default';
      }

      const bounds: AnnotationBounds = {
        x: this.X,
        y: this.Y,
        width: this.Width,
        height: this.Height,
      };

      drawAnnotation({
        ctx,
        bounds,
        mainColor: this.colorCode,
        mainOpacity: this.opacity,
        mainBorderWidth: this.borderWidth,
        state: annotationState,
        zoom,
        bleedOrange: this._isBleedingOrange,
        displayType: this.displayType,
        regionState: this.regionState,
      });

      ctx.restore();
    }
  }
  SuggestionAnnotation.prototype.elementName = 'Suggestion';

  // Register the annotation type with the annotation manager
  annotationManager.registerAnnotationType(
    SuggestionAnnotation.prototype.elementName,
    SuggestionAnnotation,
  );

  return SuggestionAnnotation;
};

/**
 * Creates a new SuggestionAnnotation instance with the specified raw annotation data
 * and configures its position and dimensions based on the provided document viewer.
 *
 * Assumes that the annotation is valid for creation based on active page and data
 * @param rawAnnot - The raw annotation data
 * @param documentViewer - The document viewer instance
 * @param padding - The amount of padding to add to the annotation bounds
 * @returns A configured SuggestionAnnotation instance or null if invalid
 */
export const createAnnotation = (
  rawAnnot: SuggestionAnnotationRaw,
  documentViewer: Core.DocumentViewer,
  SuggestionAnnotationClass: SuggestionAnnotationClassType,
  padding = 3,
): ISuggestionAnnotation => {
  if (!rawAnnot.page) {
    throw new Error('Annotation page is required');
  }
  if (!rawAnnot.loc && !rawAnnot.geometry) {
    throw new Error('Annotation location is required');
  }
  // Create new annotation instance
  const newAnnotation = new SuggestionAnnotationClass(rawAnnot);
  newAnnotation.Id = rawAnnot.id;
  newAnnotation.PageNumber = rawAnnot.page;
  newAnnotation.Hidden = true;

  // Establish location of annotation
  if (rawAnnot.loc) {
    const pageHeight = documentViewer.getPageHeight(rawAnnot.page);
    newAnnotation.X = rawAnnot.loc[0];
    newAnnotation.Y = pageHeight - rawAnnot.loc[3];
    newAnnotation.Width = rawAnnot.loc[2] - rawAnnot.loc[0];
    newAnnotation.Height = rawAnnot.loc[3] - rawAnnot.loc[1];
  } else if (rawAnnot.geometry) {
    newAnnotation.X = rawAnnot.geometry.x1;
    newAnnotation.Y = rawAnnot.geometry.y1;
    newAnnotation.Width = rawAnnot.geometry.width;
    newAnnotation.Height = rawAnnot.geometry.height;
  }

  // Add padding to make annotations easier to interact with
  // This increases the interactive area by expanding the bounds
  if (padding > 0) {
    newAnnotation.X = newAnnotation.X - padding;
    newAnnotation.Y = newAnnotation.Y - padding;
    newAnnotation.Width = newAnnotation.Width + 2 * padding;
    newAnnotation.Height = newAnnotation.Height + 2 * padding;
  }

  return newAnnotation;
};
